import { Injectable } from '@angular/core';
import axios from 'axios';
import { serialize } from 'object-to-formdata';
import { environment } from 'src/environments/environment';
import { InterestType } from '../cases/cases.service';

export type CreateBulkUploadParams = {
  client: string;
  partner: string | null;
  file: File;
  claim_file: File;
  creation_strategy: 'pro_forma' | 'start';
  name: string;
  column_associations: Record<string, string>;
  payee_case_references: string[];
  claim_references: string[];
  debtor_party_type_id?: number;
  claim_type_id?: number;
  legal_relationship_id?: number;
  product_type?: 'notice' | 'demand_letter' | 'hard_1';
  case_type?: 'invoice' | 'contract';
  interest_type?: InterestType | null;
};

export type BulkUpload = {
  id: number;
  last_start_batch_id: string | null;
  last_validation_batch_id: string | null;
  state: 'validating' | 'starting' | 'started';
  name: string;
};

export type CreateBulkUploadResponse = {
  bulk_upload: BulkUpload;
};

export type ListBulkUploadColumns = {
  required_columns: Record<string, string>;
  optional_columns: Record<string, string>;
};

export type BatchStatus = {
  total_jobs: number;
  failed_jobs: number;
  pending_jobs: number;
  wait: number | null;
  errors: null | {
    row_index: number;
    message: string;
  }[];
  bulk_upload: BulkUpload;
};

@Injectable({
  providedIn: 'root'
})
export class BulkUploadService {
  async create(params: CreateBulkUploadParams): Promise<CreateBulkUploadResponse> {
    const url = `${environment.baseUrl}/api/bulk-upload`;
    const formData = serialize(params, {
      indices: true,
    });
    const result = await axios.post<CreateBulkUploadResponse>(url, formData);

    return result.data;
  }

  async getResult(bulkUploadId: number): Promise<BatchStatus> {
    const url = `${environment.baseUrl}/api/bulk-upload/get-result/${bulkUploadId}`;
    const result = await axios.get(url);
    return result.data;
  }

  async retryFailedJobs(bulkUploadId: number): Promise<BulkUpload> {
    const url = `${environment.baseUrl}/api/bulk-upload/retry-failed-jobs/${bulkUploadId}`;
    const result = await axios.get(url);
    return result.data.bulk_upload;
  }

  async guessColumnAssociations(associations: string[]): Promise<Record<string, string>> {
    const url = `${environment.baseUrl}/api/bulk-upload/guess-column-associations`;
    const result = await axios.get<{ associations: Record<string, string>; }>(url, {
      params: {
        associations,
      },
    });

    return result.data.associations;
  }

  async listColumns(): Promise<ListBulkUploadColumns> {
    const url = `${environment.baseUrl}/api/bulk-upload/list-columns`;
    const result = await axios.get<ListBulkUploadColumns>(url);

    return result.data;
  }

  async listBulkUploadNames(name: string): Promise<string[]> {
    const result = await axios.get<{ names: string[]; }>(
      `${environment.baseUrl}/api/bulk-upload/list-names`,
      { params: { name } }
    );
    return result.data.names;
  }
}
